/**
 * Asks the API once per page load whether logos and avatars load from `/media`
 * on CloudFront (epic 2026-10-02-cloudfront-media, sub-task 04). Asks again an
 * hour before the avatars' cookies expire, when a hidden tab is shown again
 * near or past that point, and every few minutes while the call fails.
 *
 * The context's DEFAULT is "settled, off": a tree without this provider (unit
 * tests, isolated stories) presigns exactly as before, and so does the app when
 * the call fails. While the first answer is pending, `settled` is false and the
 * image hooks wait instead of presigning, so a page never asks for both.
 */
import type { MediaSessionResponse } from "@nu-assist/contracts";
import { SessionExpiredError } from "@nu-assist/shared-fe";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, createContext, useContext, useEffect, useMemo } from "react";

import {
  type MediaClient,
  MediaSessionError,
  defaultMediaClient,
} from "../../clients/media.client";
import type { MediaSession } from "./mediaUrls";

export interface MediaSessionState {
  /** True once the session is known (or failed, which counts as off). */
  settled: boolean;
  session: MediaSession | null;
}

const OFF: MediaSessionState = { settled: true, session: null };

const MediaSessionContext = createContext<MediaSessionState>(OFF);

export const MEDIA_SESSION_QUERY_KEY = ["media-session"] as const;

/** Ask again this long before the cookies expire. */
const REFRESH_BEFORE_EXPIRY_MS = 60 * 60 * 1000;

/**
 * Signed out, as the client actually reports it. Through `makeAuthedFetch` a
 * 401 tries `/auth/refresh` first; when that fails it throws
 * `SessionExpiredError` and the 401 never reaches the media client. A bare
 * `MediaSessionError(401)` only appears when the refresh worked and the retry
 * was still refused.
 *
 * Caveat: `refreshOnce` also reports a network error or a 5xx on
 * `/auth/refresh` as a failed refresh, so a transient blip reads as signed out
 * too. That is why signed out means "presign" (see `value`), never "keep the
 * last answer": avatars stay visible either way, and a tab switch or reload
 * asks again.
 */
const signedOut = (error: unknown): boolean =>
  error instanceof SessionExpiredError ||
  (error instanceof MediaSessionError && error.status === 401);

/** What the provider holds on /auth/callback until AuthCallback resets it. */
const NOT_YET: MediaSessionResponse = {
  base_url: null,
  logos: false,
  avatars: false,
  expires_at: 0,
};

/** While the session call keeps failing, ask again this often. */
const RETRY_AFTER_FAILURE_MS = 5 * 60 * 1000;

export function MediaSessionProvider({
  children,
  client,
}: {
  children: ReactNode;
  client?: MediaClient;
}) {
  const api = useMemo(() => client ?? defaultMediaClient(), [client]);
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: MEDIA_SESSION_QUERY_KEY,
    // Not on /auth/callback: the sign-in code exchange runs there, and a
    // parallel `/auth/refresh` failing with a stale refresh cookie would clear
    // the cookies that exchange just set. AuthCallback resets this query once
    // it has navigated away.
    queryFn: ({ signal }) =>
      window.location.pathname.startsWith("/auth/callback")
        ? Promise.resolve(NOT_YET)
        : api.session(signal),
    // A failed first call must not leave the tab presigning until a reload:
    // retry once, quickly (the image hooks wait while this is pending), then
    // ask again every few minutes while it keeps failing.
    // Signed out (on /login, after logout) is an answer, not a failure: no
    // retry and no polling. Sign-in asks again (AuthCallback resets this query).
    retry: (failures, error) => !signedOut(error) && failures < 1,
    retryDelay: 500,
    staleTime: Number.POSITIVE_INFINITY,
    refetchInterval: (q) => {
      if (q.state.status === "error") {
        return signedOut(q.state.error) ? false : RETRY_AFTER_FAILURE_MS;
      }
      const expiresAt = q.state.data?.expires_at;
      if (!expiresAt) return false;
      return Math.max(expiresAt * 1000 - Date.now() - REFRESH_BEFORE_EXPIRY_MS, 60_000);
    },
  });

  // Timers do not run in a background tab, and a laptop can sleep past the
  // cookies' expiry. When the tab is shown again, ask again if the cookies are
  // within the refresh margin (or the last call failed).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      const expiresAt =
        queryClient.getQueryData<MediaSessionResponse>(MEDIA_SESSION_QUERY_KEY)?.expires_at;
      const state = queryClient.getQueryState(MEDIA_SESSION_QUERY_KEY);
      const failed = state?.status === "error" && !signedOut(state.error);
      if (failed || (expiresAt && expiresAt * 1000 - Date.now() < REFRESH_BEFORE_EXPIRY_MS)) {
        void queryClient.refetchQueries({ queryKey: MEDIA_SESSION_QUERY_KEY });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [queryClient]);

  const value = useMemo<MediaSessionState>(() => {
    if (query.isPending) return { settled: false, session: null };
    // A refetch that answers "signed out" leaves the previous data in place
    // (TanStack keeps it on error). Never serve it: its cookies are gone, or
    // about to expire with no refresh coming.
    if (query.isError && signedOut(query.error)) return OFF;
    const data = query.data;
    if (!data?.base_url) return OFF;
    return {
      settled: true,
      session: {
        baseUrl: data.base_url,
        logos: data.logos,
        avatars: data.avatars,
        expiresAt: data.expires_at,
      },
    };
  }, [query.isPending, query.isError, query.error, query.data]);

  return <MediaSessionContext.Provider value={value}>{children}</MediaSessionContext.Provider>;
}

export function useMediaSession(): MediaSessionState {
  return useContext(MediaSessionContext);
}
